/**
 * GlassCard — frosted-glass card primitive. Drop-in replacement for any
 * `<div className="bg-white rounded-lg shadow border ...">`.
 *
 * Variants:
 *   - default: rich glass with hover lift
 *   - flat: no hover transform (use for static info panels)
 *   - tinted: faint coloured wash (pass `tint`)
 */
import React from 'react';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  variant?: 'default' | 'flat';
  tint?: 'emerald' | 'rose' | 'amber' | 'sky' | 'violet' | 'teal' | 'orange';
  delay?: number; // ms for entrance animation; omit to skip animation
  as?: 'div' | 'section' | 'article';
}

const tintMap: Record<NonNullable<Props['tint']>, string> = {
  emerald: 'border-emerald-300/70',
  rose:    'border-rose-300/70',
  amber:   'border-amber-300/70',
  sky:     'border-sky-300/70',
  violet:  'border-violet-300/70',
  teal:    'border-teal-300/70',
  orange:  'border-orange-300/70',
};

const GlassCard: React.FC<Props> = ({
  children,
  variant = 'default',
  tint,
  delay,
  as: Tag = 'div' as any,
  className = '',
  style,
  ...rest
}) => {
  const base = 'glass-card';
  const hover = variant === 'default' ? '' : 'glass-card-flat';
  const tintCls = tint ? tintMap[tint] : '';
  const animCls = delay !== undefined ? 'fade-up' : '';
  const animStyle = delay !== undefined ? { animationDelay: `${delay}ms`, ...style } : style;

  return (
    <Tag
      className={`${base} ${hover} ${tintCls} ${animCls} ${className}`}
      style={animStyle}
      {...(rest as any)}
    >
      {children}
    </Tag>
  );
};

export default GlassCard;
